/**
 * InputRoundDetail — Facilitator view for a single input round.
 *
 * Responses tab: participant list with submission status; add/remove participants.
 * Analysis tab: per-item, per-attribute distribution; Close / Reveal / Aggregate actions.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Users2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  CheckCircle2,
  Clock,
  UserPlus,
  X,
  Layers,
  BarChart2,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  Send,
  Search,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useInputRoundsStore } from '../../../stores/prioritization/inputRoundsStore';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { InputRoundStatus } from '../../../types/prioritization/inputRounds';

// ============================================================================
// Status helpers
// ============================================================================

function statusLabel(status: InputRoundStatus) {
  if (status === 'open') return { text: 'Open', color: 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border-[var(--green)]' };
  if (status === 'closed') return { text: 'Closed', color: 'text-[var(--text-sec)] bg-[var(--hover-tint)] border-[var(--rule)]' };
  return { text: 'Revealed', color: 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]' };
}

// ============================================================================
// Add Participant — pick from workspace members (same directory as stewardship / ownership)
// ============================================================================

function AddParticipantForm({
  roundId,
  excludeUserIds,
  onDone,
}: {
  roundId: string;
  excludeUserIds: string[];
  onDone: () => void;
}) {
  const addParticipant = useInputRoundsStore((s) => s.addParticipant);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const { users, loading, error: usersError, refetch } = useSystemUsers(
    currentWorkspaceId ? { workspaceId: currentWorkspaceId } : undefined
  );
  const [query, setQuery] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (excludeUserIds.includes(u.id)) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.metadata?.title?.toLowerCase().includes(q) ?? false) ||
        (u.metadata?.department?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [users, query, excludeUserIds]);

  const handlePick = async (userId: string, displayName: string) => {
    setAddingId(userId);
    setError(null);
    try {
      await addParticipant(roundId, userId, displayName);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add participant');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <p className="text-[11px] text-[var(--text-ter)]">
        Choose someone in this workspace. They need a login; invites use their account id.
      </p>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-8 pr-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
        />
      </div>
      {usersError && (
        <div className="flex items-center justify-between gap-2 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-2 py-1.5">
          <span>Could not load members: {usersError}</span>
          <button type="button" onClick={() => refetch()} className="text-[var(--text-ter)] font-medium hover:underline shrink-0">
            Retry
          </button>
        </div>
      )}
      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      <div className="border border-[var(--rule)] max-h-52 overflow-y-auto bg-[var(--bg-raised)]">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-[var(--text-ter)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading workspace members…
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] text-center py-8 px-3">
            {users.length === 0
              ? 'No workspace members found. Open a workspace in the app header, or ask an admin to add people to this workspace.'
              : excludeUserIds.length >= users.length
                ? 'Everyone in the workspace is already a participant.'
                : 'No matches. Try another search.'}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--rule)]">
            {filtered.map((u) => {
              const label = u.name?.trim() || u.email || u.id;
              const busy = addingId === u.id;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handlePick(u.id, label)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--slate-dim)] disabled:opacity-60 transition-colors"
                  >
                    <div className="h-8 w-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center text-[var(--text-sec)] text-xs font-semibold shrink-0">
                      {label.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[var(--text-pri)] truncate">{u.name || '—'}</p>
                      <p className="text-[11px] text-[var(--text-ter)] truncate">{u.email}</p>
                    </div>
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)] shrink-0" />
                    ) : (
                      <UserPlus className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Attribute analysis card
// ============================================================================

function AttributeAnalysisCard({
  roundId,
  itemId,
  itemTitle,
  attributeKey,
  attributeLabel,
  scaleId,
  isBlindAndNotRevealed,
}: {
  roundId: string;
  itemId: string;
  itemTitle: string;
  attributeKey: string;
  attributeLabel: string;
  scaleId?: string | null;
  isBlindAndNotRevealed: boolean;
}) {
  const proposals = useValueProposalStore((s) => s.proposals);
  const aggregateProposals = useValueProposalStore((s) => s.aggregateProposals);
  const userId = useUserStore((s) => s.userId);
  const currentRole = useUserStore((s) => s.currentRole);
  const [isAggregating, setIsAggregating] = useState(false);
  const [aggResult, setAggResult] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const roundProposals = useMemo(
    () =>
      proposals.filter(
        (p) =>
          p.itemId === itemId &&
          p.attributeKey === attributeKey &&
          p.roundId === roundId &&
          p.status === 'proposed'
      ),
    [proposals, itemId, attributeKey, roundId]
  );

  const numericVals = roundProposals
    .map((p) => Number(p.proposedValue))
    .filter((n) => !isNaN(n));
  const isNumeric = numericVals.length > 0 && numericVals.length === roundProposals.length;
  const mean =
    isNumeric && numericVals.length > 0
      ? (numericVals.reduce((a, b) => a + b, 0) / numericVals.length).toFixed(2)
      : null;

  const handleAggregate = async () => {
    if (roundProposals.length < 2) return;
    setIsAggregating(true);
    try {
      const result = await aggregateProposals({
        proposalIds: roundProposals.map((p) => p.id),
        aggregationMethod: 'mean',
        acceptedBy: userId,
        acceptorRole: currentRole as 'workspace-admin',
        scaleId: scaleId ?? undefined,
      });
      setAggResult(result.saved ? `Aggregated → ${result.value.value}` : result.error ?? 'Failed');
    } finally {
      setIsAggregating(false);
    }
  };

  if (isBlindAndNotRevealed) {
    return (
      <div className="flex items-center justify-between bg-[var(--surface)] border border-[var(--rule)] px-3 py-2">
        <span className="text-sm font-medium text-[var(--text-sec)]">{attributeLabel}</span>
        <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
          <EyeOff className="h-3.5 w-3.5" />
          {roundProposals.length} inputs hidden
        </span>
      </div>
    );
  }

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <BarChart2 className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="font-medium text-sm text-[var(--text-pri)]">{attributeLabel}</span>
          <span className="text-[11px] text-[var(--text-ter)]">
            {roundProposals.length} input{roundProposals.length !== 1 ? 's' : ''}
            {mean ? ` · mean ${mean}` : ''}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />}
      </button>

      {expanded && (
        <div className="px-4 py-3 space-y-3">
          {roundProposals.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)] italic">No inputs yet for {itemTitle}.</p>
          ) : (
            <div className="space-y-1.5">
              {roundProposals.map((p) => (
                <div key={p.id} className="flex items-start gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-[var(--text-pri)]">{String(p.proposedValue)}</span>
                    <span className="ml-2 text-[11px] text-[var(--text-ter)]">
                      {p.proposedByName || p.proposedBy.slice(0, 8)}
                    </span>
                    {p.confidence != null && (
                      <span className="ml-1 text-[11px] text-[var(--text-ter)]">
                        {Math.round(p.confidence * 100)}% conf.
                      </span>
                    )}
                    {p.rationale && (
                      <p className="text-[11px] text-[var(--text-ter)] italic mt-0.5">"{p.rationale}"</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {roundProposals.length >= 2 && (
            <div className="pt-2 border-t border-[var(--rule)]">
              {aggResult ? (
                <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium">{aggResult}</p>
              ) : (
                <button
                  type="button"
                  disabled={isAggregating}
                  onClick={handleAggregate}
                  className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1 disabled:opacity-50"
                >
                  {isAggregating ? 'Aggregating…' : '→ Aggregate (mean) and accept'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function InputRoundDetail() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();

  const {
    rounds,
    fetchFromSupabase,
    setRoundStatus,
    removeParticipant,
    getItemsForRound,
    getParticipantsForRound,
    getResponseRate,
  } = useInputRoundsStore();

  const proposals = useValueProposalStore((s) => s.proposals);
  const fetchProposals = useValueProposalStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);

  const [activeTab, setActiveTab] = useState<'responses' | 'analysis'>('responses');
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  useEffect(() => {
    fetchFromSupabase();
    fetchProposals();
    fetchItems();
    fetchAttributes();
  }, [fetchFromSupabase, fetchProposals, fetchItems, fetchAttributes]);

  const round = rounds.find((r) => r.id === roundId);
  const roundItemList = getItemsForRound(roundId ?? '');
  const participantList = getParticipantsForRound(roundId ?? '');
  const rate = getResponseRate(roundId ?? '');

  const isBlindAndNotRevealed = round?.visibilityMode === 'blind' && round.status !== 'revealed';

  // Unique items in this round
  const uniqueItemIds = useMemo(
    () => [...new Set(roundItemList.map((ri) => ri.itemId))],
    [roundItemList]
  );

  const handleStatusChange = async (newStatus: InputRoundStatus) => {
    if (!roundId) return;
    setStatusError(null);
    try {
      await setRoundStatus(roundId, newStatus);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  if (!round) {
    return (
      <div className="py-16 text-center text-[var(--text-ter)]">
        Round not found.{' '}
        <Link to="/prioritization/input-rounds" className="text-[var(--text-ter)] hover:underline">
          Back to rounds
        </Link>
      </div>
    );
  }

  const sl = statusLabel(round.status);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Users2 className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title={round.name}
        subtitle={round.description || 'Crowd input round'}
        actions={
          <div className="flex items-center gap-2">
            {/* Submit link */}
            {round.status === 'open' && (
              <button
                onClick={() => navigate(`/prioritization/input-rounds/${roundId}/submit`)}
                className="btn btn-secondary flex items-center gap-1.5 text-sm"
              >
                <Send className="h-4 w-4" />
                Submit inputs
              </button>
            )}
            {/* Status actions */}
            {round.status === 'open' && (
              <button
                onClick={() => handleStatusChange('closed')}
                className="btn btn-secondary flex items-center gap-1.5 text-sm"
              >
                <Lock className="h-4 w-4" />
                Close round
              </button>
            )}
            {round.status === 'closed' && round.visibilityMode === 'blind' && (
              <button
                onClick={() => handleStatusChange('revealed')}
                className="btn btn-primary flex items-center gap-1.5 text-sm"
              >
                <Eye className="h-4 w-4" />
                Reveal inputs
              </button>
            )}
            {round.status === 'closed' && round.visibilityMode === 'open' && (
              <button
                onClick={() => handleStatusChange('revealed')}
                className="btn btn-secondary flex items-center gap-1.5 text-sm text-[var(--text-ter)]"
              >
                <CheckCircle2 className="h-4 w-4" />
                Mark revealed
              </button>
            )}
            {round.status === 'revealed' && (
              <button
                onClick={() => handleStatusChange('open')}
                className="btn btn-secondary flex items-center gap-1.5 text-sm"
              >
                <Unlock className="h-4 w-4" />
                Reopen
              </button>
            )}
          </div>
        }
      />

      {/* Back link + meta bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={() => navigate('/prioritization/input-rounds')}
          className="flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeft className="h-4 w-4" />
          All rounds
        </button>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${sl.color}`}>
          {sl.text}
        </span>
        <span className="text-[11px] text-[var(--text-ter)] flex items-center gap-1 capitalize">
          {round.visibilityMode === 'blind' ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {round.visibilityMode} visibility
        </span>
        {round.deadline && (
          <span className="text-[11px] text-[var(--text-ter)] flex items-center gap-1">
            <CalendarDays className="h-3 w-3" />
            Deadline: {new Date(round.deadline).toLocaleString()}
          </span>
        )}
        {rate.total > 0 && (
          <span className="text-[11px] text-[var(--text-ter)] flex items-center gap-2">
            <div className="w-20 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--slate)] rounded-full" style={{ width: `${rate.percent}%` }} />
            </div>
            {rate.responded}/{rate.total} responded
          </span>
        )}
        {statusError && <p className="text-xs text-[var(--coral)]">{statusError}</p>}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[var(--rule)]">
        {([
          { id: 'responses', label: 'Responses', icon: <Users2 className="h-4 w-4" /> },
          { id: 'analysis', label: 'Analysis', icon: <BarChart2 className="h-4 w-4" /> },
        ] as { id: 'responses' | 'analysis'; label: string; icon: React.ReactNode }[]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-[var(--slate)] text-[var(--text-ter)]'
                : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Responses tab */}
      {activeTab === 'responses' && (
        <div className="space-y-4">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[var(--surface)] border-b border-[var(--rule)]">
              <h3 className="text-sm font-medium text-[var(--text-sec)]">
                Participants ({participantList.length})
              </h3>
              {round.status === 'open' && (
                <button
                  type="button"
                  onClick={() => setShowAddParticipant((v) => !v)}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Add
                </button>
              )}
            </div>

            {showAddParticipant && round.status === 'open' && (
              <div className="px-4 pb-3">
                <AddParticipantForm
                  roundId={roundId ?? ''}
                  excludeUserIds={participantList.map((p) => p.userId)}
                  onDone={() => setShowAddParticipant(false)}
                />
              </div>
            )}

            {participantList.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--text-ter)]">
                No participants added yet.
              </div>
            ) : (
              <div className="divide-y divide-[var(--rule)]">
                {participantList.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center text-[var(--text-sec)] text-xs font-semibold">
                        {(p.userName ?? p.userId).slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--text-pri)]">{p.userName ?? p.userId}</p>
                        <p className="text-[11px] text-[var(--text-ter)]">
                          Invited {new Date(p.invitedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {p.submittedAt ? (
                        <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Submitted
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
                          <Clock className="h-3.5 w-3.5" />
                          Pending
                        </span>
                      )}
                      {round.status === 'open' && (
                        <button
                          onClick={() => removeParticipant(p.id)}
                          className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] rounded"
                          title="Remove participant"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Items in scope */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[var(--surface)] border-b border-[var(--rule)]">
              <h3 className="text-sm font-medium text-[var(--text-sec)]">
                Items in scope ({uniqueItemIds.length})
              </h3>
              {round.status === 'open' && (
                <Link
                  to={`/prioritization/input-rounds/${roundId}/submit`}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                >
                  <Send className="h-3.5 w-3.5" />
                  Go to submission
                </Link>
              )}
            </div>
            {roundItemList.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[var(--text-ter)]">
                No items scoped yet. Use the submit page to add items.
              </p>
            ) : (
              <div className="divide-y divide-[var(--rule)]">
                {uniqueItemIds.map((itemId) => {
                  const item = items.find((i) => i.id === itemId);
                  const attrKeys = [...new Set(roundItemList.filter((ri) => ri.itemId === itemId).map((ri) => ri.attributeKey))];
                  const inputCount = proposals.filter(
                    (p) => p.itemId === itemId && p.roundId === roundId && p.status === 'proposed'
                  ).length;
                  return (
                    <div key={itemId} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-[var(--text-pri)]">{item?.title ?? itemId}</p>
                        <p className="text-[11px] text-[var(--text-ter)]">
                          {attrKeys.map((k) => attributes.find((a) => a.key === k)?.label ?? k).join(', ')}
                        </p>
                      </div>
                      <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
                        <Layers className="h-3.5 w-3.5" />
                        {inputCount} input{inputCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analysis tab */}
      {activeTab === 'analysis' && (
        <div className="space-y-6">
          {isBlindAndNotRevealed && (
            <div className="flex items-center gap-2 px-4 py-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
              <EyeOff className="h-4 w-4 shrink-0" />
              This round uses blind visibility. Inputs are hidden until you reveal the round.
            </div>
          )}

          {uniqueItemIds.length === 0 ? (
            <div className="py-12 text-center text-[var(--text-ter)] text-sm">
              No items in scope for this round.
            </div>
          ) : (
            uniqueItemIds.map((itemId) => {
              const item = items.find((i) => i.id === itemId);
              const itemRoundItems = roundItemList.filter((ri) => ri.itemId === itemId);
              return (
                <div key={itemId} className="space-y-2">
                  <h3 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
                    {item?.title ?? itemId}
                  </h3>
                  <div className="space-y-2">
                    {itemRoundItems.map((ri) => {
                      const attr = attributes.find((a) => a.key === ri.attributeKey);
                      return (
                        <AttributeAnalysisCard
                          key={ri.id}
                          roundId={roundId ?? ''}
                          itemId={itemId}
                          itemTitle={item?.title ?? itemId}
                          attributeKey={ri.attributeKey}
                          attributeLabel={attr?.label ?? ri.attributeKey}
                          scaleId={ri.scaleId}
                          isBlindAndNotRevealed={isBlindAndNotRevealed}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default InputRoundDetail;
